import ModalSearchLayout from "@/components/layout/ModalSearchLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import Table from "@/components/ui/Table";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { useState, useEffect } from "react";
import { useItemCodeSearch } from "@/hooks/standard/st_item_code/useItemCode";

interface TableColumn {
  key: string;
  header: string;
  align?: "left" | "center" | "right";
}

interface SearchOption {
  value: string;
  label: string;
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: any) => void;
  title: string; // 모달 제목
  columns: TableColumn[]; // 테이블 컬럼
  data?: any[]; // 동적 데이터
  searchOptions: SearchOption[]; // 검색 옵션
  onSearch?: (keyword: string, category: string) => void; // 검색 함수
  onSearchButtonClick?: (keyword: string) => void; // 검색 버튼 클릭 핸들러 (검색어 전달)
  initialSearchKeyword?: string; // 초기 검색어
  isLoadingInitial?: boolean; // 첫 페이지 로딩
  isLoadingMore?: boolean; // 추가 페이지 로딩
  onInfiniteScroll?: () => void; // 무한스크롤 콜백
  hasMore?: boolean; // 더 많은 데이터가 있는지
}

export default function SearchModal({
  isOpen,
  onClose,
  onSelect,
  title,
  columns,
  data = [],
  searchOptions,
  onSearch,
  onSearchButtonClick,
  initialSearchKeyword,
  isLoadingInitial = false,
  isLoadingMore = false,
  onInfiniteScroll,
  hasMore = true,
}: SearchModalProps) {
  const [searchKeyword, setSearchKeyword] = useState(initialSearchKeyword || "");
  const [searchCategory, setSearchCategory] = useState("");
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [tableVer, setTableVer] = useState(0);

  // 부모에서 전달받은 데이터만 사용
  const rows = data;

  const infiniteScrollPage = useInfiniteScrollPage({
    data: rows as any[],
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
     if (hasMore) onInfiniteScroll?.();
   },
  });


  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchKeyword, searchCategory);
    } else {
      if (onSearchButtonClick) {
        onSearchButtonClick(searchKeyword);
        setActualSearchValue(searchKeyword);
      }
    }
    setActualSearchValue(searchKeyword);
  };

  useEffect(() => {
    setTableVer(v => v + 1);
  }, [actualSearchValue]);

  // initialSearchKeyword가 존재하면 자동으로 조회
  useEffect(() => {
    if (isOpen && initialSearchKeyword) {
      setSearchKeyword(initialSearchKeyword);
      // handleSearch 함수를 호출하지 않고 직접 검색 함수 호출
      if (onSearch) {
        onSearch(initialSearchKeyword, searchCategory);
        setActualSearchValue(initialSearchKeyword);
      } else if (onSearchButtonClick) {
        onSearchButtonClick(initialSearchKeyword);
        setActualSearchValue(initialSearchKeyword);
      }
    }
  }, [initialSearchKeyword, isOpen]);

  const handleRowDoubleClick = (item: any) => {
    onSelect(item);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalSearchLayout isOpen={isOpen} onClose={onClose} title={title} width="w-[815px]">
      <div className="h-full flex flex-col">
        {/* 검색 조건 */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex gap-3 items-end overflow-y-auto overscroll-contain">
            <div className="flex-1">
              <InputBox
                label="검색어"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="검색어를 입력하세요"
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </div>
            <div className="flex-shrink-0">
              <Button variant="primary" onClick={handleSearch}>
                검색
              </Button>
            </div>
          </div>
        </div>

        {/* 테이블 */}
        <div className="flex-1 min-h-0">
            <Table
              showRowNumber
              highlightOnClick={true}
              key={`item-code-search-table-v${tableVer}`}
              {...infiniteScrollPage.tableProps}
              columns={columns}
              data={rows as any[]}
              onRowDoubleClick={handleRowDoubleClick}
              onRowSelect={setSelectedRows}
              maxHeight="calc(100vh - 200px)"
              loadingMore={isLoadingMore}
              loading={isLoadingInitial}
              emptyMessage="검색 결과가 없습니다."
            />
        </div>
      </div>
    </ModalSearchLayout>
  );
}